import { primaryActionStyle } from '../primaryActionStyle';
import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { NmIsoDateField } from '../NmIsoDateField';
import { useAthleteMetrics } from '../../hooks/useAthleteMetrics';
import { chartManager } from '../../services/api';
import type { PmcChartSettings } from './pmcSettings';
import { SEG_INPUT, SEG_TRACK } from './segmentControlStyles';

export interface PmcRecalculateBlockProps {
  userId: number;
  chartId: number;
  setting: PmcChartSettings;
  defaultStart: string;
  defaultEnd: string;
  active?: boolean;
  onEditFtp?: () => void;
  onBeforeRecalculate?: () => void | Promise<void>;
  onSuccess?: () => void | Promise<void>;
}

export function PmcRecalculateBlock({
  userId,
  chartId,
  setting,
  defaultStart,
  defaultEnd,
  active = true,
  onEditFtp,
  onBeforeRecalculate,
  onSuccess,
}: PmcRecalculateBlockProps) {
  const { t } = useTranslation();
  const { ftp } = useAthleteMetrics(userId);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [fullHistory, setFullHistory] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setFullHistory(true);
    setError(null);
  }, [active, defaultStart, defaultEnd]);

  const pmc = setting.pmc ?? {};

  async function handleRecalculate() {
    setBusy(true);
    setError(null);
    try {
      await onBeforeRecalculate?.();
      await chartManager.recalculateTrainingMetrics(chartId, userId, {
        start_date: startDate,
        end_date: endDate,
        full_history: fullHistory,
        ctl_period: pmc.fitness_CTL_constant ?? 42,
        atl_period: pmc.fatigue_ATL_constant ?? 7,
        initial_ctl: pmc.fitness_CTL_start ?? 0,
        initial_atl: pmc.fatigue_ATL_start ?? 0,
      });
      await onSuccess?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('charts.pmcRecalculate.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--fg-1)' }}>
          {t('charts.pmcRecalculate.title')}
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 4, lineHeight: 1.5 }}>
          {t('charts.pmcRecalculate.subtitle')}
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13 }}>
        <input
          type="checkbox"
          checked={fullHistory}
          onChange={(e) => setFullHistory(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <span>
          <strong>{t('charts.pmcRecalculate.fullHistory')}</strong>
          <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 4 }}>
            {t('charts.pmcRecalculate.fullHistoryHint')}
          </div>
        </span>
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--fg-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {t('charts.pmcRecalculate.dateRangeSection')}
        </div>
        {fullHistory && (
          <div style={{ fontSize: 11, color: 'var(--fg-4)', lineHeight: 1.45 }}>
            {t('charts.pmcRecalculate.rangeDisabledHint')}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            opacity: fullHistory ? 0.55 : 1,
            pointerEvents: fullHistory ? 'none' : 'auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-4)', minWidth: 36 }}>
              {t('charts.from')}
            </span>
            <div style={{ ...SEG_TRACK, flex: 1 }}>
              <NmIsoDateField value={startDate} onChange={setStartDate} style={SEG_INPUT} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-4)', minWidth: 36 }}>
              {t('charts.to')}
            </span>
            <div style={{ ...SEG_TRACK, flex: 1 }}>
              <NmIsoDateField value={endDate} onChange={setEndDate} style={SEG_INPUT} />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: '12px 14px',
          borderRadius: 12,
          background: 'var(--surface-sunken)',
          fontSize: 11,
          color: 'var(--fg-3)',
          lineHeight: 1.55,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <div>
            {t('charts.pmcRecalculate.ftpLabel')}:{' '}
            <strong className="ltr-island">{ftp != null ? `${ftp} W` : '—'}</strong>
          </div>
          {onEditFtp && (
            <button type="button" onClick={onEditFtp} style={BTN_LINK}>
              {t('charts.pmcRecalculate.editFtp')}
            </button>
          )}
        </div>
        <div style={{ marginTop: 6 }}>{t('charts.pmcRecalculate.ftpHint')}</div>
        <div style={{ marginTop: 8 }}>
          CTL τ={pmc.fitness_CTL_constant ?? 42} · ATL τ={pmc.fatigue_ATL_constant ?? 7}
        </div>
        <div style={{ marginTop: 6 }}>
          {t(fullHistory ? 'charts.pmcRecalculate.seedHintFull' : 'charts.pmcRecalculate.seedHintRange')}
        </div>
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--danger, #dc2626)' }}>{error}</div>}

      <button type="button" onClick={handleRecalculate} disabled={busy} style={BTN_PRIMARY}>
        {busy ? t('charts.pmcRecalculate.running') : t('charts.pmcRecalculate.run')}
      </button>
    </div>
  );
}

const BTN_LINK: CSSProperties = {
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--text-accent)',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};

const BTN_PRIMARY: CSSProperties = {
  width: '100%',
  padding: '10px 16px',
  borderRadius: 12,
  border: 'none',
  ...primaryActionStyle,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};
